import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { documentAPI } from "../services/api";
import { toast } from "react-toastify";
import {
  FileText,
  Calendar,
  Search,
  Plus,
  Trash2,
  Eye,
  Download,
  ExternalLink,
} from "lucide-react";
import { safeFormatDateOnly } from "../utils/dateUtils";

function Documents() {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    const filterDocuments = () => {
      let filtered = documents;

      if (searchTerm) {
        filtered = filtered.filter(
          (doc) =>
            doc.documentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (doc.description &&
              doc.description.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      if (filterType && filterType !== "all") {
        filtered = filtered.filter((doc) => doc.documentType === filterType);
      }

      setFilteredDocuments(filtered);
    };

    filterDocuments();
  }, [documents, searchTerm, filterType]);

  const fetchDocuments = async () => {
    try {
      const response = await documentAPI.getMyDocuments();
      console.log("Documents fetch response:", response);
      const data = response.data;
      console.log("Documents data:", data);

      // Handle both direct array and nested documents array
      let documentsArray;
      if (Array.isArray(data)) {
        documentsArray = data;
      } else if (data && Array.isArray(data.documents)) {
        documentsArray = data.documents;
      } else {
        documentsArray = [];
      }

      console.log("Setting documents array:", documentsArray);
      setDocuments(documentsArray);
    } catch (error) {
      toast.error("Failed to fetch documents");
      console.error("Documents error:", error);
      setDocuments([]); // Ensure documents is always an array
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (documentId, documentName) => {
    if (window.confirm(`Are you sure you want to delete "${documentName}"?`)) {
      try {
        await documentAPI.deleteDocument(documentId);
        if (Array.isArray(documents)) {
          setDocuments(documents.filter((doc) => doc._id !== documentId));
        }
        toast.success("Document deleted successfully");
      } catch (error) {
        toast.error("Failed to delete document");
        console.error("Delete error:", error);
      }
    }
  };

  const handleDownload = async (doc) => {
    try {
      // First fetch the full document with data
      const response = await documentAPI.getDocument(doc._id, true);
      const fullDoc = response.data;

      if (fullDoc?.documentData) {
        // Create download link for base64 data
        const link = document.createElement("a");
        link.href = fullDoc.documentData;
        link.download = fullDoc.documentName || "document";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Signed document downloaded successfully");
      } else {
        toast.error("Document data not available for download");
      }
    } catch (error) {
      toast.error("Failed to download document");
      console.error("Download error:", error);
    }
  };

  const handleShareForVerification = (doc) => {
    const verificationUrl = `${window.location.origin}/verify/${doc._id}`;

    // Try modern clipboard API first, fallback to older methods
    const copyToClipboard = async (text) => {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        } else {
          // Fallback for older browsers or non-secure contexts
          const textArea = document.createElement("textarea");
          textArea.value = text;
          textArea.style.position = "absolute";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          try {
            const successful = document.execCommand("copy");
            document.body.removeChild(textArea);
            return successful;
          } catch (err) {
            document.body.removeChild(textArea);
            return false;
          }
        }
      } catch (err) {
        return false;
      }
    };

    copyToClipboard(verificationUrl).then((success) => {
      if (success) {
        toast.success("Verification link copied to clipboard!");
      } else {
        // Show the URL in a modal/alert if copying fails
        const message = `Copy this verification link:\n\n${verificationUrl}`;
        if (window.confirm(message + "\n\nClick OK to close this dialog.")) {
          // User clicked OK
        }
        toast.info("Please copy the verification link manually");
      }
    });
  };

  const getDocumentTypes = () => {
    if (!Array.isArray(documents)) {
      return [];
    }
    const types = [
      ...new Set(documents.map((doc) => doc.documentType).filter(Boolean)),
    ];
    return types;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">My Documents</h1>
              <p className="mt-2 text-gray-600">
                Manage and view your signed documents
              </p>
            </div>
            <Link
              to="/documents/sign"
              className="btn-primary flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Sign New Document</span>
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="card p-6 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search documents..."
                  className="pl-10 input-field"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                className="input-field"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                {getDocumentTypes().map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Documents List */}
        {filteredDocuments.length === 0 ? (
          <div className="card p-12 text-center">
            <FileText className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {documents.length === 0
                ? "No documents yet"
                : "No documents match your filters"}
            </h3>
            <p className="text-gray-500 mb-6">
              {documents.length === 0
                ? "Get started by signing your first document"
                : "Try adjusting your search or filter criteria"}
            </p>
            {documents.length === 0 && (
              <Link to="/documents/sign" className="btn-primary">
                Sign Your First Document
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDocuments.map((doc) => (
              <div
                key={doc._id}
                className="card p-6 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-8 w-8 text-primary-600" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {doc.documentName}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {doc.description || "No description"}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-4 w-4" />
                        <span>Signed: {safeFormatDateOnly(doc.signedAt)}</span>
                      </div>
                      <span>Type: {doc.documentType || "PDF"}</span>
                      <span>
                        Size:{" "}
                        {doc.fileSize
                          ? (doc.fileSize / 1024).toFixed(1) + " KB"
                          : "Unknown"}
                      </span>
                      {doc.category && <span>Category: {doc.category}</span>}
                    </div>

                    {doc.tags && doc.tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {doc.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Signed
                    </span>

                    <div className="flex space-x-1">
                      <Link
                        to={`/documents/${doc._id}`}
                        className="p-2 text-gray-400 hover:text-primary-600 rounded-md hover:bg-gray-100"
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Link>

                      <button
                        onClick={() => handleDownload(doc)}
                        className="p-2 text-gray-400 hover:text-blue-600 rounded-md hover:bg-gray-100"
                        title="Download signed document"
                      >
                        <Download className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleShareForVerification(doc)}
                        className="p-2 text-gray-400 hover:text-green-600 rounded-md hover:bg-gray-100"
                        title="Share for public verification"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDelete(doc._id, doc.documentName)}
                        className="p-2 text-gray-400 hover:text-red-600 rounded-md hover:bg-gray-100"
                        title="Delete document"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Summary */}
        {documents.length > 0 && (
          <div className="mt-8 text-center text-sm text-gray-500">
            Showing {filteredDocuments.length} of {documents.length} documents
          </div>
        )}
      </div>
    </div>
  );
}

export default Documents;
